import React, { useState, useEffect } from 'react';
import { Barcode } from '@capacitor-mlkit/barcode-scanning';
import { lookupProductByUPC, Product, ProductLookupResponse } from '../services/productService';
import { ManualProductEntry } from './ManualProductEntry';
import '../styles/components/barcodeResult.css';

interface BarcodeResultModalProps {
  barcode: Barcode;
  token: string;
  onAddToInventory: (product: Product) => void;
  onAddToShoppingList?: (product: Product) => void;
  onCancel: () => void;
  hasShoppingCollection?: boolean;
}

export const BarcodeResultModal: React.FC<BarcodeResultModalProps> = ({
  barcode,
  token,
  onAddToInventory,
  onAddToShoppingList,
  onCancel,
  hasShoppingCollection = false
}) => {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<ProductLookupResponse | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);

  useEffect(() => {
    const lookupProduct = async () => {
      if (!barcode.rawValue) {
        setResult({
          success: false,
          error: 'Invalid barcode data',
          allow_manual_entry: false
        });
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await lookupProductByUPC(token, barcode.rawValue);
      setResult(response);
      setLoading(false);

      // If not found, automatically show manual entry
      if (!response.success && response.allow_manual_entry) {
        setShowManualEntry(true);
      }
    };

    lookupProduct();
  }, [barcode, token]);

  const handleManualEntrySave = (product: Product) => {
    setResult({
      success: true,
      product,
      cached: false,
      source: 'manual',
      allow_manual_entry: false
    });
    setShowManualEntry(false);
  };

  if (loading) {
    return (
      <div className="barcode-result-modal">
        <div className="modal-content">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Looking up product...</p>
            <p className="upc-code">UPC: {barcode.rawValue}</p>
          </div>
        </div>
      </div>
    );
  }

  if (showManualEntry) {
    return (
      <ManualProductEntry
        upc={barcode.rawValue || ''}
        token={token}
        onSave={handleManualEntrySave}
        onCancel={onCancel}
      />
    );
  }

  if (!result || !result.success || !result.product) {
    return (
      <div className="barcode-result-modal">
        <div className="modal-content">
          <div className="error-state">
            <h2>Product Not Found</h2>
            <p className="upc-code">UPC: {barcode.rawValue}</p>
            <p className="error-message">{result?.error || 'Unable to find product information'}</p>
            
            <div className="action-buttons">
              <button 
                onClick={() => setShowManualEntry(true)}
                className="btn-primary"
              >
                Enter Manually
              </button>
              <button onClick={onCancel} className="btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { product } = result;

  return (
    <div className="barcode-result-modal">
      <div className="modal-content">
        <div className="product-info">
          {product.image_url && (
            <div className="product-image">
              <img src={product.image_url} alt={product.name} />
            </div>
          )}
          
          <div className="product-details">
            <h2>{product.name}</h2>
            {product.brand && <p className="brand">{product.brand}</p>}
            {product.size && <p className="size">{product.size}</p>}
            {product.category && <p className="category">{product.category}</p>}
            {product.price && <p className="price">${product.price.toFixed(2)}</p>}
            <p className="upc-code">UPC: {product.upc}</p>
            
            {result.source && (
              <p className="source-info">
                {result.cached ? '📦 Cached' : '🌐 Live'} • Source: {result.source}
              </p>
            )}
          </div>
        </div>

        <div className="action-buttons">
          <button 
            onClick={() => onAddToInventory(product)}
            className="btn-primary"
          >
            {hasShoppingCollection ? 'Add' : 'Add to Inventory'}
          </button>
          <button onClick={onCancel} className="btn-tertiary">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
